// ─── Theme tokens ────────────────────────────────────────────────────────────
const DRAW_COLORS = {
    active: '#CCF54E',
    activePoint: '#CCF45E',   // intentionally slightly different hex used in the original
    inactive: '#F0F0F0',
    inactiveStroke: '#FFFFFF',
    transparent: 'rgba(0,0,0,0)',
} as const;

// ─── Styles ──────────────────────────────────────────────────────────────────
export const mapboxDrawStyles: object[] = [
    // ── Polygons: Inactive (drawn but not selected) ──────────────────────────
    {
        id: 'gl-draw-polygon-fill-inactive',
        type: 'fill',
        filter: ['all',
            ['==', 'active', 'false'],
            ['==', '$type', 'Polygon'],
            ['!=', 'mode', 'static'],
        ],
        paint: {
            'fill-color': DRAW_COLORS.inactive,
            'fill-opacity': 0.05,
        },
    },
    {
        id: 'gl-draw-polygon-stroke-inactive',
        type: 'line',
        filter: ['all',
            ['==', 'active', 'false'],
            ['==', '$type', 'Polygon'],
            ['!=', 'mode', 'static'],
        ],
        paint: {
            'line-color': DRAW_COLORS.inactiveStroke,
            'line-width': 1,
            'line-dasharray': [1, 2],
        },
    },

    // ── Polygons: Active (selected) ──────────────────────────────────────────
    {
        id: 'gl-draw-polygon-fill-active',
        type: 'fill',
        filter: ['all',
            ['==', 'active', 'true'],
            ['==', '$type', 'Polygon'],
        ],
        paint: {
            'fill-color': DRAW_COLORS.active,
            'fill-opacity': 0.2,
        },
    },
    {
        id: 'gl-draw-polygon-stroke-active-dashed',
        type: 'line',
        filter: ['all',
            ['==', 'active', 'true'],
            ['==', '$type', 'Polygon'],
        ],
        paint: {
            'line-color': DRAW_COLORS.active,
            'line-width': 1,
            'line-dasharray': [1, 2],
        },
    },

    // ── Points: Active ───────────────────────────────────────────────────────
    // Ring (bottom layer)
    {
        id: 'gl-draw-point-active-ring',
        type: 'circle',
        filter: ['all',
            ['==', '$type', 'Point'],
            ['==', 'meta', 'feature'],
            ['==', 'active', 'true'],
        ],
        paint: {
            'circle-radius': 8,
            'circle-color': DRAW_COLORS.transparent,
            'circle-stroke-color': DRAW_COLORS.activePoint,
            'circle-stroke-width': 1,
        },
    },
    // Soft fill
    {
        id: 'gl-draw-point-active-outer',
        type: 'circle',
        filter: ['all',
            ['==', '$type', 'Point'],
            ['==', 'meta', 'feature'],
            ['==', 'active', 'true'],
        ],
        paint: {
            'circle-radius': 6,
            'circle-color': DRAW_COLORS.activePoint,
            'circle-opacity': 0.2,
        },
    },
    // Center dot
    {
        id: 'gl-draw-point-active-inner',
        type: 'circle',
        filter: ['all',
            ['==', '$type', 'Point'],
            ['==', 'meta', 'feature'],
            ['==', 'active', 'true'],
        ],
        paint: {
            'circle-radius': 2.5,
            'circle-color': DRAW_COLORS.activePoint,
        },
    },

    // ── Points: Inactive ─────────────────────────────────────────────────────
    {
        id: 'gl-draw-point-inactive-outer',
        type: 'circle',
        filter: ['all',
            ['==', '$type', 'Point'],
            ['==', 'meta', 'feature'],
            ['==', 'active', 'false'],
        ],
        paint: {
            'circle-radius': 6,
            'circle-color': DRAW_COLORS.inactive,
            'circle-opacity': 0.2,
        },
    },
    {
        id: 'gl-draw-point-inactive-inner',
        type: 'circle',
        filter: ['all',
            ['==', '$type', 'Point'],
            ['==', 'meta', 'feature'],
            ['==', 'active', 'false'],
        ],
        paint: {
            'circle-radius': 2.5,
            'circle-color': DRAW_COLORS.inactive,
            'circle-opacity': 1,
        },
    },

    // ── Lines: Inactive ──────────────────────────────────────────────────────
    {
        id: 'gl-draw-line-inactive',
        type: 'line',
        filter: ['all',
            ['==', 'active', 'false'],
            ['==', '$type', 'LineString'],
            ['!=', 'mode', 'static'],
        ],
        paint: {
            'line-color': DRAW_COLORS.inactiveStroke,
            'line-width': 1,
            'line-dasharray': [1, 2],
        },
    },
    // ── Lines: Active ────────────────────────────────────────────────────────
    {
        id: 'gl-draw-line-active',
        type: 'line',
        filter: ['all',
            ['==', 'active', 'true'],
            ['==', '$type', 'LineString'],
        ],
        paint: {
            'line-color': DRAW_COLORS.active,
            'line-width': 1,
            'line-dasharray': [1, 2],
        },
    },

    // ── Vertices ─────────────────────────────────────────────────────────────
    {
        id: 'gl-draw-vertex-inner-active',
        type: 'circle',
        filter: ['all',
            ['==', '$type', 'Point'],
            ['==', 'meta', 'vertex'],
            ['==', 'active', 'true'],
        ],
        paint: {
            'circle-radius': 3.5,
            'circle-color': DRAW_COLORS.active,
            'circle-opacity': 1,
        },
    },
    {
        id: 'gl-draw-vertex-inner-drawing',
        type: 'circle',
        filter: [
            'all',
            ['==', '$type', 'Point'],
            ['==', 'meta', 'vertex'],
            ['!=', 'mode', 'simple_select'],
            ['==', 'active', 'false']
        ],
        paint: {
            'circle-radius': 3.5,
            'circle-color': DRAW_COLORS.active,
            'circle-opacity': 1
        }
    },
    {
        id: "gl-draw-midpoint-drawing",
        type: "circle",
        filter: [
            "all",
            ["==", "$type", "Point"],
            ["==", "meta", "midpoint"]
        ],
        paint: {
            "circle-radius": 3.5,
            "circle-color": DRAW_COLORS.active,
            "circle-opacity": 0.9,
        }
    },
];
